import { createColumnHelper } from '@tanstack/react-table'
import { startCase } from 'lodash-es'
import { Link } from 'react-router'
import {
  type AgentRunsListItem,
  type AgentRunSummary,
  CategoryValue,
  EMPTY_VALUE,
  formatCost,
  formatCount,
  formatTimestamp,
  getAgentRunUrl,
  getRunTotalCost,
  MATCH_SIDE_LABELS,
  type MatchSide,
  MatchStatusChip,
  MatchVerdictChip,
  RunStatusChip,
} from '~/entities/document-agent'
import { Button, DataTable, Text } from '~/shared/ui'

const columnHelper = createColumnHelper<AgentRunsListItem>()

function MatchSideLine({
  run,
  side,
  isFinalSide,
}: {
  run: AgentRunSummary
  side: MatchSide
  isFinalSide: boolean
}) {
  const summary = run[side]
  if (!summary) return null
  const decision = [summary.decision ?? summary.outcome, summary.candidate, summary.confidence]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span
        className={isFinalSide ? 'text-[13px] leading-5' : 'text-text-secondary text-xs leading-4'}
      >
        {`${MATCH_SIDE_LABELS[side]}: ${decision || EMPTY_VALUE}`}
      </span>
      {isFinalSide && <MatchVerdictChip verdict={summary.verdict} />}
    </div>
  )
}

const columns = [
  columnHelper.accessor('document_date', {
    header: 'Document date',
    cell: (info) => <span className="whitespace-nowrap">{formatTimestamp(info.getValue())}</span>,
  }),
  columnHelper.accessor('short_key', {
    header: 'Document ID',
    cell: (info) => (
      <Link
        className="text-text-link whitespace-nowrap no-underline hover:underline"
        to={getAgentRunUrl(info.getValue())}
        onClick={(event) => event.stopPropagation()}
      >
        {info.getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor('source', {
    header: 'Source',
    cell: (info) => info.getValue() || EMPTY_VALUE,
  }),
  columnHelper.accessor('review_status', {
    header: 'Review status',
    cell: (info) => {
      const status = info.getValue()
      return <span className="whitespace-nowrap">{status ? startCase(status) : EMPTY_VALUE}</span>
    },
  }),
  columnHelper.accessor('today_category', {
    header: "Today's category",
    cell: (info) => <CategoryValue category={info.getValue()} />,
  }),
  columnHelper.accessor('final_category', {
    header: 'Final category',
    cell: (info) => <CategoryValue category={info.getValue()} />,
  }),
  columnHelper.display({
    id: 'run_label',
    header: 'Run label',
    cell: ({ row }) => {
      const [latestRun, ...olderRuns] = row.original.runs
      if (!latestRun) return EMPTY_VALUE
      return (
        <div className="flex flex-col">
          <span className="text-[13px] leading-5 wrap-anywhere">{latestRun.run_label}</span>
          {olderRuns.length > 0 && (
            <Text
              className="text-text-secondary"
              appearance="CAPTION"
            >
              {`${olderRuns.length + 1} runs`}
            </Text>
          )}
        </div>
      )
    },
  }),
  columnHelper.display({
    id: 'run_status',
    header: 'Run status',
    cell: ({ row }) => {
      const latestRun = row.original.runs[0]
      return latestRun ? <RunStatusChip status={latestRun.run_status} /> : EMPTY_VALUE
    },
  }),
  columnHelper.display({
    id: 'agent_category',
    header: 'Agent category',
    cell: ({ row }) => {
      const agentCategory = row.original.runs[0]?.agent_category
      const finalCategory = row.original.final_category
      return (
        <CategoryValue
          category={agentCategory}
          isMismatch={!!agentCategory && !!finalCategory && agentCategory !== finalCategory}
        />
      )
    },
  }),
  columnHelper.display({
    id: 'matching',
    header: 'Patient matching',
    cell: ({ row }) => {
      const latestRun = row.original.runs[0]
      if (!latestRun?.match_status) return EMPTY_VALUE
      const finalSide = row.original.final_side
      const sides = (
        finalSide === 'pa' ? (['pa', 'claim'] as const) : (['claim', 'pa'] as const)
      ).filter((side) => latestRun[side])
      return (
        <div className="flex flex-col items-start gap-0.5">
          {latestRun.match_status !== 'completed' && (
            <MatchStatusChip status={latestRun.match_status} />
          )}
          {sides.map((side) => (
            <MatchSideLine
              key={side}
              run={latestRun}
              side={side}
              isFinalSide={side === finalSide}
            />
          ))}
        </div>
      )
    },
  }),
  columnHelper.display({
    id: 'cost',
    header: 'Cost',
    cell: ({ row }) => {
      const latestRun = row.original.runs[0]
      if (!latestRun) return EMPTY_VALUE
      return (
        <div className="flex flex-col">
          <span className="whitespace-nowrap">{formatCost(getRunTotalCost(latestRun))}</span>
          {typeof latestRun.match_cost_usd === 'number' && (
            <Text
              className="text-text-secondary whitespace-nowrap"
              appearance="CAPTION"
            >
              {`matching ${formatCost(latestRun.match_cost_usd)}`}
            </Text>
          )}
        </div>
      )
    },
  }),
  columnHelper.display({
    id: 'model_calls',
    header: 'Model calls',
    cell: ({ row }) => formatCount(row.original.runs[0]?.model_calls),
  }),
]

export function AgentRunsTable({
  items,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  items: AgentRunsListItem[]
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
}) {
  return (
    <DataTable
      columns={columns}
      data={items}
      withStickyHeader
      getRowUrl={(item) => getAgentRunUrl(item.short_key)}
      noResultsMessage={
        hasNextPage
          ? 'No matches among the documents scanned so far. Load more to keep looking.'
          : 'No documents with agent runs match these filters.'
      }
      footer={
        hasNextPage ? (
          <div className="flex justify-center py-4">
            <Button
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        ) : null
      }
    />
  )
}
